import { FC, useEffect } from "react";
import { twMerge } from "tailwind-merge";

import { switchOrg } from "api/api-server-actions";
import { IconText } from "mds/components/IconText";
import { Listbox } from "mds/components/Listbox";
import { HomeHouseIcon } from "mds/icons";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import { orgSelectors } from "store/selectors";
import { aiStateActions } from "store/slices/ai";
import { toastLocalizedOperationCatcher } from "utils/alerts";
import { GENERAL_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { BASE_SECTION_URL } from "utils/urls";

/**
 * A dropdown list of orgs.
 */
export type OrgSelectProps = {
  className?: string;
};

export const OrgSelect: FC<OrgSelectProps> = ({ className }) => {
  const orgs = useAppSelector(orgSelectors.selectAll);
  const dispatch = useAppDispatch();

  const currentOrg = orgs.find((org) => org.current === true);
  const hasChoices = orgs.length > 1;

  useEffect(() => {
    storeApi.orgs.list();
  }, []);

  if (orgs.length <= 0 || !currentOrg) {
    return null;
  }

  if (!hasChoices) {
    return (
      <IconText
        className="text-sm font-medium text-black-tint-20"
        iconStart={<HomeHouseIcon />}
        text={currentOrg?.name}
      />
    );
  }

  const items = orgs.map((org) => ({
    id: org.id,
    title: org.name,
    iconStart: <HomeHouseIcon className="shrink-0" />,
  }));

  const onSelectOrg = (orgId: string) => {
    switchOrg(orgId)
      .then((resp) => {
        trackEvent("New Institution selected", {
          ...GENERAL_EVENT_CATEGORY,
          newOrgName: resp.data.name,
        });
        dispatch(aiStateActions.clearAiChatMessages());

        // Redirect to the same page only if it's not a section page
        // which would be specific to the org we were just on.
        const newUrl = window.location.pathname.includes(BASE_SECTION_URL)
          ? window.location.origin
          : window.location.origin + window.location.pathname;

        window.location.href = newUrl;
      })
      .catch(toastLocalizedOperationCatcher("switching_org"));
  };

  return (
    <Listbox
      buttonKind="secondary"
      buttonSize="s"
      className={twMerge("max-md:max-w-48", className)}
      items={items}
      openMenuEventName="Switch Institution"
      selectedValue={currentOrg.id}
      onSelectionChange={(item) => onSelectOrg(item.id)}
    />
  );
};
